import React, { useState, useEffect } from 'react';
import { FaWarehouse, FaBook, FaTruckLoading, FaUserCircle, FaUsers, FaFilter, FaDownload } from 'react-icons/fa';
import api from '../../utils/api';
import LoadingSpinner from '../../components_shared/LoadingSpinner';

const COLORS = {
  khi: '#0ea5e9',  // Karachi
  hyd: '#3b82f6',  // Hyderabad
  suk: '#6366f1',  // Sukkur
  lar: '#8b5cf6',  // Larkana
  mir: '#ec4899',  // Mirpurkhas
  sba: '#10b981',  // Shaheed Benazirabad
};

export default function InventoryHeatmap() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [hoveredDivision, setHoveredDivision] = useState(null);

  // Filters state
  const [subject, setSubject] = useState('All');
  const [grade, setGrade] = useState('All');
  const [publisher, setPublisher] = useState('All');

  useEffect(() => {
    fetchHeatmapData();
  }, []);

  const fetchHeatmapData = () => {
    setLoading(true);
    api.get('/api/v1/reports/inventory-heatmap')
      .then(res => {
        setData(res.data || []);
        if (res.data && res.data.length > 0) {
          // Select Karachi as default detailed view
          setSelectedDivision(res.data.find(d => d.id === 'karachi') || res.data[0]);
        }
      })
      .catch(err => console.error('Error fetching heatmap:', err))
      .finally(() => setLoading(false));
  };

  // Dynamically calculate filtered volumes to make the map and stats feel alive and reactive
  const getFilteredMetrics = (division) => {
    if (!division) return null;
    
    // Simulate real-time filter coefficients
    let factor = 1.0;
    if (subject !== 'All') factor *= 0.28;
    if (grade !== 'All') factor *= 0.35;
    if (publisher !== 'All') factor *= 0.42;

    const booksStored = Math.round(division.booksStored * factor);
    const reservedBooks = Math.round(booksStored * 0.32);
    const dispatchQty = Math.round(division.dispatchQty * (factor * 0.8));
    const activeDispatches = factor < 1.0 ? Math.max(1, Math.round(division.activeDispatches * 0.5)) : division.activeDispatches;

    return {
      ...division,
      booksStored,
      reservedBooks,
      dispatchQty,
      activeDispatches,
    };
  };

  if (loading) return <LoadingSpinner />;

  // Aggregate global filtered totals
  const aggregatedStats = data.reduce((acc, div) => {
    const metrics = getFilteredMetrics(div);
    return {
      totalStored: acc.totalStored + metrics.booksStored,
      totalReserved: acc.totalReserved + metrics.reservedBooks,
      activeDispatches: acc.activeDispatches + metrics.activeDispatches,
      totalWarehouses: acc.totalWarehouses + metrics.activeWarehouses,
    };
  }, { totalStored: 0, totalReserved: 0, activeDispatches: 0, totalWarehouses: 0 });

  // Density calculations for color scales
  const maxStored = Math.max(...data.map(d => getFilteredMetrics(d).booksStored), 1);

  const getHeatmapColor = (booksStored, maxVal) => {
    const ratio = booksStored / maxVal;
    if (ratio > 0.8) return 'rgba(99, 102, 241, 0.85)';    // Indigo
    if (ratio > 0.5) return 'rgba(59, 130, 246, 0.75)';    // Bright Blue
    if (ratio > 0.3) return 'rgba(14, 165, 233, 0.65)';    // Tealish Blue
    if (ratio > 0.15) return 'rgba(16, 185, 129, 0.55)';   // Emerald Green
    return 'rgba(16, 185, 129, 0.3)';                      // Low concentration soft green
  };

  const handleExportCSV = () => {
    const headers = ["Division", "Active Warehouses", "Capacity Sqft", "Books Stored", "Reserved Books", "Active Dispatches", "Dispatch Qty", "Staff Headcount", "Logistics Manager"].join(",");
    const rows = data.map(d => {
      const m = getFilteredMetrics(d);
      return `"${m.division}",${m.activeWarehouses},${m.capacitySqft},${m.booksStored},${m.reservedBooks},${m.activeDispatches},${m.dispatchQty},${m.headcount},"${m.manager}"`;
    }).join("\n");
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows}`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `STBB_Regional_Inventory_Report_${subject}_${grade}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeDetails = getFilteredMetrics(selectedDivision);
  const activeHoverDetails = getFilteredMetrics(hoveredDivision);

  return (
    <div className="inventory-page inventory-heatmap-page" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      {/* 🔮 GLASSMORPHIC HEADER */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, color: '#1e293b', letterSpacing: '-0.5px' }}>Sindh Regional Inventory Heatmap</h2>
          <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '14px' }}>
            Interactive geographical stock densities, capacities, and live logistics flows.
          </p>
        </div>
        <button 
          onClick={handleExportCSV}
          style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 20px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(37, 99, 235, 0.25)',
            transition: 'all 0.2s ease-in-out',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.25)';
          }}
        >
          <FaDownload /> Export CSV Data
        </button>
      </div>

      {/* 🚀 KPI PANEL */}
      <div className="row g-4 mb-4">
        {[
          { label: 'Aggregate Stock Stored', val: aggregatedStats.totalStored.toLocaleString(), icon: FaBook, color: '#6366f1', glow: 'rgba(99, 102, 241, 0.15)' },
          { label: 'Reserved Buffers', val: aggregatedStats.totalReserved.toLocaleString(), icon: FaWarehouse, color: '#0ea5e9', glow: 'rgba(14, 165, 233, 0.15)' },
          { label: 'Active In-Transit Items', val: aggregatedStats.activeDispatches.toLocaleString(), icon: FaTruckLoading, color: '#10b981', glow: 'rgba(16, 185, 129, 0.15)' },
          { label: 'Monitored Warehouses', val: aggregatedStats.totalWarehouses, icon: FaWarehouse, color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.15)' },
        ].map((k, i) => (
          <div className="col-md-3" key={i}>
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              padding: '20px 24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.01)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div>
                <span style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>{k.label}</span>
                <h3 style={{ margin: '8px 0 0 0', fontWeight: '700', color: '#1e293b', fontSize: '26px' }}>{k.val}</h3>
              </div>
              <div style={{
                background: k.glow,
                color: k.color,
                borderRadius: '12px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                <k.icon />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        {/* 🗺️ MAP CONSOLE PANEL */}
        <div className="col-md-8">
          <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)',
            minHeight: '520px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            {/* Filters Header bar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: '16px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontWeight: '600' }}>
                <FaFilter style={{ color: '#6366f1' }} /> Interactive Filters
              </div>
              
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* Subject Selector */}
                <select 
                  value={subject} 
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#f8fafc',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="All">All Subjects</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Sindhi">Sindhi</option>
                  <option value="English">English</option>
                  <option value="Science">Science</option>
                </select>

                {/* Grade Selector */}
                <select 
                  value={grade} 
                  onChange={(e) => setGrade(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#f8fafc',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="All">All Classes</option>
                  <option value="Primary">Primary (Class 1-5)</option>
                  <option value="Middle">Middle (Class 6-8)</option>
                  <option value="Secondary">Secondary (Class 9-10)</option>
                </select>

                {/* Publisher Selector */}
                <select 
                  value={publisher} 
                  onChange={(e) => setPublisher(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#f8fafc',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="All">All Publishers</option>
                  <option value="The Times Press">The Times Press</option>
                  <option value="Salman Agha">Salman Agha</option>
                  <option value="Oxford">Oxford Press</option>
                </select>
              </div>
            </div>

            {/* SVG Visualizer Arena */}
            <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <svg 
                viewBox="0 0 600 500" 
                style={{ width: '100%', maxHeight: '440px', filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.04))' }}
              >
                <defs>
                  {/* Neon teal hover fill gradient */}
                  <linearGradient id="neon-teal" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>

                  {/* Standard shadow definitions */}
                  <filter id="glow-filter" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="8" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {data.map((item) => {
                  const metrics = getFilteredMetrics(item);
                  const isHovered = hoveredDivision?.id === item.id;
                  const isSelected = selectedDivision?.id === item.id;
                  
                  // Setup coordinate mapping
                  let dPath = "";
                  let textCoord = { x: 0, y: 0 };

                  if (item.id === 'larkana') {
                    dPath = "M 130 50 L 260 50 L 240 170 L 170 190 L 110 130 Z";
                    textCoord = { x: 170, y: 100 };
                  } else if (item.id === 'sukkur') {
                    dPath = "M 260 50 L 460 70 L 480 200 L 330 210 L 240 170 Z";
                    textCoord = { x: 350, y: 120 };
                  } else if (item.id === 'shaheed_benazirabad') {
                    dPath = "M 170 190 L 240 170 L 330 210 L 340 300 L 220 320 L 180 270 Z";
                    textCoord = { x: 250, y: 245 };
                  } else if (item.id === 'mirpurkhas') {
                    dPath = "M 330 210 L 480 200 L 510 350 L 380 395 L 340 300 Z";
                    textCoord = { x: 420, y: 290 };
                  } else if (item.id === 'hyderabad') {
                    dPath = "M 180 270 L 220 320 L 340 300 L 380 395 L 260 450 L 190 380 Z";
                    textCoord = { x: 270, y: 365 };
                  } else if (item.id === 'karachi') {
                    dPath = "M 100 280 L 180 270 L 190 380 L 140 420 L 80 370 Z";
                    textCoord = { x: 135, y: 335 };
                  }

                  const fillColor = isHovered || isSelected 
                    ? 'url(#neon-teal)' 
                    : getHeatmapColor(metrics.booksStored, maxStored);

                  return (
                    <g key={item.id}>
                      {/* Active shadow layer under the selected shape */}
                      {(isHovered || isSelected) && (
                        <path
                          d={dPath}
                          fill="none"
                          stroke="#0ea5e9"
                          strokeWidth="8"
                          opacity="0.3"
                          style={{ filter: 'url(#glow-filter)' }}
                        />
                      )}
                      
                      <path
                        d={dPath}
                        fill={fillColor}
                        stroke="#fff"
                        strokeWidth={isHovered || isSelected ? "3.5" : "1.8"}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                        onMouseEnter={() => setHoveredDivision(item)}
                        onMouseLeave={() => setHoveredDivision(null)}
                        onClick={() => setSelectedDivision(item)}
                      />

                      {/* District Division Label */}
                      <text
                        x={textCoord.x}
                        y={textCoord.y}
                        textAnchor="middle"
                        fill={isHovered || isSelected ? '#ffffff' : '#1e293b'}
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          transition: 'all 0.2s ease',
                          fillShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        {item.division}
                      </text>
                      
                      {/* Sub-percentage detail label */}
                      <text
                        x={textCoord.x}
                        y={textCoord.y + 15}
                        textAnchor="middle"
                        fill={isHovered || isSelected ? 'rgba(255,255,255,0.85)' : '#475569'}
                        style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      >
                        {Math.round((metrics.booksStored / aggregatedStats.totalStored) * 100)}% Stock
                      </text>
                    </g>
                  );
                })}
              </svg>

              {/* Dynamic hover glassmorphic float card */}
              {activeHoverDetails && (
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'rgba(15, 23, 42, 0.95)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  width: '200px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                  zIndex: 10,
                  backdropFilter: 'blur(8px)',
                  animation: 'fadeIn 0.2s ease'
                }}>
                  <h6 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#0ea5e9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9' }}></span>
                    {activeHoverDetails.division}
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Stored:</span>
                      <strong style={{ color: '#e2e8f0' }}>{activeHoverDetails.booksStored.toLocaleString()}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Warehouses:</span>
                      <strong style={{ color: '#e2e8f0' }}>{activeHoverDetails.activeWarehouses}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Dispatches:</span>
                      <strong style={{ color: '#10b981' }}>{activeHoverDetails.activeDispatches} Active</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Density scale legend */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '16px',
              borderTop: '1px solid #f1f5f9',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <span>Density Legend:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Low</span>
                <div style={{
                  width: '180px',
                  height: '10px',
                  borderRadius: '5px',
                  background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.55) 25%, rgba(14, 165, 233, 0.65) 50%, rgba(59, 130, 246, 0.75) 75%, rgba(99, 102, 241, 0.85) 100%)'
                }}></div>
                <span>High Capacity</span>
              </div>
            </div>
          </div>
        </div>

        {/* 📋 REGIONAL DETAIL PANEL DRAWER */}
        <div className="col-md-4">
          {activeDetails ? (
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Division Dispatch Desk</h4>
                  <span style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    borderRadius: '30px',
                    padding: '4px 14px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>{activeDetails.id}</span>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  borderRadius: '16px',
                  padding: '20px',
                  color: '#fff',
                  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                  marginBottom: '24px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ position: 'absolute', right: '-15px', bottom: '-20px', opacity: 0.1, fontSize: '100px' }}>
                    <FaWarehouse />
                  </div>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>Active Regional Warehouse</span>
                  <h4 style={{ margin: '4px 0 16px 0', fontWeight: '700', color: '#f8fafc' }}>
                    {activeDetails.division} Logistics Hub
                  </h4>
                  
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>Capacity</span>
                      <strong style={{ fontSize: '16px', color: '#38bdf8' }}>{activeDetails.capacitySqft.toLocaleString()} <span style={{ fontSize: '10px' }}>Sq.Ft</span></strong>
                    </div>
                    <div style={{ width: '1px', background: '#334155' }}></div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>Active Force</span>
                      <strong style={{ fontSize: '16px', color: '#34d399' }}>{activeDetails.headcount} Operators</strong>
                    </div>
                  </div>
                </div>

                {/* Logistics statistics list */}
                <h5 style={{ fontWeight: '700', color: '#334155', fontSize: '14px', marginBottom: '14px' }}>Storage Statistics</h5>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                  {[
                    { label: 'Books Currently Stored', val: activeDetails.booksStored.toLocaleString(), sub: 'In physical custody' },
                    { label: 'Reserved / Safety Buffers', val: activeDetails.reservedBooks.toLocaleString(), sub: 'Earmarked for future dispatches' },
                    { label: 'Total Volume Dispatched', val: activeDetails.dispatchQty.toLocaleString(), sub: 'Shipped to Talukas' },
                  ].map((stat, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9'
                    }}>
                      <div>
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155' }}>{stat.label}</span>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{stat.sub}</span>
                      </div>
                      <strong style={{ fontSize: '15px', color: '#1e293b' }}>{stat.val}</strong>
                    </div>
                  ))}
                </div>

                {/* Manager info */}
                <h5 style={{ fontWeight: '700', color: '#334155', fontSize: '14px', marginBottom: '14px' }}>Administrative Desk</h5>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #f1f5f9',
                  padding: '14px 16px',
                }}>
                  <div style={{ fontSize: '32px', color: '#6366f1' }}>
                    <FaUserCircle />
                  </div>
                  <div>
                    <strong style={{ display: 'block', fontSize: '13px', color: '#1e293b' }}>{activeDetails.manager}</strong>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Logistics Division Superintendent</span>
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#065f46',
                fontSize: '12px'
              }}>
                <span style={{ display: 'block', width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', flexShrink: 0 }}></span>
                <span>Active logistics routes are monitored. {activeDetails.activeDispatches} incoming transport trucks are on scheduled ETA paths.</span>
              </div>
            </div>
          ) : (
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              height: '100%'
            }}>
              Select a division on the map to show comprehensive logistical parameters.
            </div>
          )}
        </div>
      </div>

      {/* 📊 SUMMARY STATISTICS SHEET */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)',
        marginTop: '24px'
      }}>
        <h4 style={{ margin: '0 0 16px 0', fontWeight: '700', color: '#1e293b' }}>Sindh Regional Inventory Matrix</h4>
        <div className="table-responsive">
          <table className="table" style={{ margin: 0, fontSize: '14px' }}>
            <thead>
              <tr style={{ color: '#64748b', fontWeight: '600', borderBottom: '2px solid #f1f5f9' }}>
                <th style={{ padding: '12px 16px' }}>Administrative Division</th>
                <th style={{ padding: '12px 16px' }}>Active Warehouses</th>
                <th style={{ padding: '12px 16px' }}>Covered Capacity</th>
                <th style={{ padding: '12px 16px' }} className="text-end">Books Stored</th>
                <th style={{ padding: '12px 16px' }} className="text-end">Reserved Buffer</th>
                <th style={{ padding: '12px 16px' }} className="text-end">Active Dispatches</th>
                <th style={{ padding: '12px 16px' }}>Division Manager</th>
              </tr>
            </thead>
            <tbody>
              {data.map((div, i) => {
                const metrics = getFilteredMetrics(div);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' }}>
                    <td style={{ padding: '16px', fontWeight: '600', color: '#1e293b' }}>{metrics.division}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        background: '#f1f5f9',
                        color: '#475569',
                        borderRadius: '20px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>{metrics.activeWarehouses} Hubs</span>
                    </td>
                    <td style={{ padding: '16px', color: '#475569' }}>{metrics.capacitySqft.toLocaleString()} Sq.Ft</td>
                    <td style={{ padding: '16px', fontWeight: '600', color: '#6366f1' }} className="text-end">{metrics.booksStored.toLocaleString()}</td>
                    <td style={{ padding: '16px', color: '#0ea5e9' }} className="text-end">{metrics.reservedBooks.toLocaleString()}</td>
                    <td style={{ padding: '16px', color: '#10b981', fontWeight: '600' }} className="text-end">{metrics.activeDispatches} Active</td>
                    <td style={{ padding: '16px', color: '#475569' }}>{metrics.manager}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
